import { Button } from "@babackapp/ui/components/button";
import { Skeleton } from "@babackapp/ui/components/skeleton";
import { PlusIcon } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";
import ExampleFileInput from "./example-file-input";
import ImportMappingRow from "./import-mapping-row";
import {
  ShopImportFormValues,
  ShopImportMappingColumnType,
} from "./import-settings-form-types";

type MappingField = ShopImportFormValues["mapping"][number];

type ImportMappingsFormProps = {
  isLoading: boolean;
  setIsLoading: (b: boolean) => void;
};

export default function ImportMappingsForm({
  isLoading,
  setIsLoading,
}: ImportMappingsFormProps) {
  const { control } = useFormContext<ShopImportFormValues>();
  const mapping = useFieldArray({
    control,
    name: "mapping",
  });

  // `mapping.update` addresses the array by position, which is not the column
  // number as soon as rows are added by hand or rebuilt from a saved mapping.
  const patch = (position: number, values: Partial<MappingField>) => {
    mapping.update(position, { ...mapping.fields[position], ...values });
  };

  const addColumn = () => {
    const nextIndex = mapping.fields.reduce(
      (max, field) => Math.max(max, field.fieldIndex + 1),
      0,
    );
    mapping.append({
      mapTo: null,
      fieldIndex: nextIndex,
      fieldName: "",
      exampleValue: "",
    });
  };

  if (isLoading) {
    return <ImportMappingsFormLoading />;
  }

  if (!mapping.fields.length) {
    return (
      <ImportMappingsFormDisabled onLoad={setIsLoading} onManual={addColumn} />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-lg max-h-[600px] overflow-auto flex flex-col gap-2 bg-muted/50 border-border border p-2">
        {mapping.fields.map((field, position) => (
          <ImportMappingRow
            key={field.id}
            title={field.fieldName}
            subtitle={field.exampleValue}
            index={field.fieldIndex}
            mapTo={field.mapTo}
            regexSuccess={field.regexSuccess}
            updateMapTo={(mapTo: ShopImportMappingColumnType) =>
              patch(position, { mapTo })
            }
            updateRegex={(regexSuccess: string) =>
              patch(position, { regexSuccess })
            }
            updateIndex={(fieldIndex: number) =>
              patch(position, { fieldIndex })
            }
            remove={() => mapping.remove(position)}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        onClick={addColumn}
      >
        <PlusIcon className="size-4" />
        Add a column
      </Button>
    </div>
  );
}

function ImportMappingsFormLoading() {
  return (
    <div className="flex flex-col gap-2">
      <ImportMappingRowSkeleton />
      <ImportMappingRowSkeleton />
      <ImportMappingRowSkeleton />
      <ImportMappingRowSkeleton />
    </div>
  );
}

type ImportMappingsFormDisabledProps = {
  onLoad?: (b: boolean) => void;
  onManual: () => void;
};

function ImportMappingsFormDisabled({
  onLoad,
  onManual,
}: ImportMappingsFormDisabledProps) {
  return (
    <div className="rounded-lg w-full relative">
      <div className="blur-sm">
        <ImportMappingsFormLoading />
      </div>
      <div className="absolute px-4 flex flex-col items-center justify-center gap-2 inset-0">
        <ExampleFileInput onLoad={onLoad} />
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={onManual}
        >
          or map the columns manually
        </Button>
      </div>
    </div>
  );
}

function ImportMappingRowSkeleton() {
  return (
    <div className="border-border/50 border rounded-md px-4 py-2 flex items-center gap-4">
      <Skeleton className="h-4 w-4" />
      <div className="flex flex-col">
        <Skeleton className="h-4 w-16 mb-1" />
        <Skeleton className="h-3 w-12" />
      </div>
      <Skeleton className="h-4 w-4 mx-auto" />
      <Skeleton className="h-8 w-[150px]" />
    </div>
  );
}
